/**
 * TaskForm component - form for creating/editing tasks
 */

import { useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { clsx } from 'clsx';
import { Calendar, Tag, User } from 'lucide-react';
import * as Dialog from '@radix-ui/react-dialog';
import { X } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import LabelPicker from './LabelPicker';
import { AssigneePicker, type AssigneeOption } from './AssigneePicker';
import Badge from '@components/ui/Badge';
import { useTaskStore } from '@stores/taskStore';
import { useBoardStore } from '@stores/boardStore';
import { useAuthStore } from '@stores/authStore';
import { projectsApi, type ProjectMember, type Label } from '@services/api';

const taskSchema = z.object({
  title: z.string().min(1, 'Title is required').max(500),
  description: z.string().max(5000).optional(),
  taskType: z.string().default('task'),
  priority: z.enum(['urgent', 'high', 'medium', 'low', 'none']).default('none'),
  assigneeId: z.string().uuid().optional().nullable(),
  assigneeContactId: z.string().uuid().optional().nullable(),
  dueDate: z.string().optional().nullable(),
  estimatedMinutes: z.number().min(0).optional().nullable(),
  labelIds: z.array(z.string()).optional(),
});

type TaskFormData = z.infer<typeof taskSchema>;

interface TaskFormProps {
  open: boolean;
  onClose: () => void;
  onSubmit: (data: TaskFormData) => Promise<void>;
  initialData?: Partial<TaskFormData> & { labels?: Label[] };
  columnId?: string;
  isEdit?: boolean;
}

const priorityOptions = [
  { value: 'urgent', label: 'Urgent', color: 'bg-red-500' },
  { value: 'high', label: 'High', color: 'bg-orange-500' },
  { value: 'medium', label: 'Medium', color: 'bg-yellow-500' },
  { value: 'low', label: 'Low', color: 'bg-blue-500' },
  { value: 'none', label: 'None', color: 'bg-neutral-300' },
];

const taskTypeOptions = [
  { value: 'task', label: 'Task' },
  { value: 'bug', label: 'Bug' },
  { value: 'story', label: 'Story' },
  { value: 'epic', label: 'Epic' },
];

export default function TaskForm({
  open,
  onClose,
  onSubmit,
  initialData,
  isEdit = false,
}: TaskFormProps) {
  const { t } = useTranslation();
  const { labels: availableLabels, fetchLabels, createLabel } = useTaskStore();
  const { currentBoard } = useBoardStore();
  const { currentOrgId } = useAuthStore();
  const [selectedLabels, setSelectedLabels] = useState<Label[]>(
    initialData?.labels || []
  );
  const [projectMembers, setProjectMembers] = useState<ProjectMember[]>([]);
  const [selectedAssignee, setSelectedAssignee] = useState<AssigneeOption | null>(null);

  const {
    register,
    handleSubmit,
    watch,
    setValue,
    reset,
    formState: { errors, isSubmitting },
  } = useForm<TaskFormData>({
    resolver: zodResolver(taskSchema),
    defaultValues: {
      title: initialData?.title || '',
      description: initialData?.description || '',
      taskType: initialData?.taskType || 'task',
      priority: initialData?.priority || 'none',
      assigneeId: initialData?.assigneeId || null,
      assigneeContactId: initialData?.assigneeContactId || null,
      dueDate: initialData?.dueDate || null,
      estimatedMinutes: initialData?.estimatedMinutes || null,
      labelIds: initialData?.labels?.map((l) => l.id) || [],
    },
  });

  const priority = watch('priority');

  // Fetch labels and project members when the form opens
  useEffect(() => {
    if (open && currentBoard?.projectId) {
      void fetchLabels(currentBoard.projectId);
      projectsApi.listMembers(currentBoard.projectId)
        .then(members => setProjectMembers(members))
        .catch(err => console.error('Failed to load project members:', err));
    }
  }, [open, currentBoard?.projectId, fetchLabels]);

  // Reset selected labels when form data changes
  useEffect(() => {
    setSelectedLabels(initialData?.labels || []);
  }, [initialData?.labels]);

  const handleFormSubmit = async (data: TaskFormData) => {
    try {
      // Include selected label IDs and assignee in submission
      const submitData = {
        ...data,
        labelIds: selectedLabels.map((l) => l.id),
        // Include assignee data based on selection
        assigneeId: selectedAssignee?.type === 'user' ? selectedAssignee.id : null,
        assigneeContactId: selectedAssignee?.type === 'contact' ? selectedAssignee.id : null,
      };
      await onSubmit(submitData);
      reset();
      setSelectedLabels([]);
      setSelectedAssignee(null);
      onClose();
    } catch (error) {
      console.error('Failed to submit task:', error);
    }
  };

  const handleClose = () => {
    reset();
    setSelectedLabels([]);
    setSelectedAssignee(null);
    onClose();
  };

  const handleAssigneeChange = (assignee: AssigneeOption | null) => {
    setSelectedAssignee(assignee);
  };

  const handleAddLabel = (labelId: string) => {
    const label = availableLabels.find((l) => l.id === labelId);
    if (label && !selectedLabels.some((l) => l.id === labelId)) {
      setSelectedLabels([...selectedLabels, label]);
    }
  };

  const handleRemoveLabel = (labelId: string) => {
    setSelectedLabels(selectedLabels.filter((l) => l.id !== labelId));
  };

  const handleCreateLabel = async (data: { name: string; color: string }) => {
    if (!currentBoard?.projectId) throw new Error('No project selected');
    return createLabel(currentBoard.projectId, data);
  };

  return (
    <Dialog.Root open={open} onOpenChange={(isOpen) => !isOpen && handleClose()}>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 z-50 bg-black/50 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0" />
        <Dialog.Content
          className={clsx(
            'fixed left-1/2 top-1/2 z-50 w-full max-w-lg -translate-x-1/2 -translate-y-1/2',
            'rounded-xl bg-white p-6 shadow-xl dark:bg-neutral-900',
            'data-[state=open]:animate-in data-[state=closed]:animate-out',
            'data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0',
            'data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95',
            'data-[state=closed]:slide-out-to-left-1/2 data-[state=closed]:slide-out-to-top-[48%]',
            'data-[state=open]:slide-in-from-left-1/2 data-[state=open]:slide-in-from-top-[48%]'
          )}
        >
          <Dialog.Title className="text-lg font-semibold text-neutral-900 dark:text-white">
            {isEdit ? t('task.edit') : t('task.create')}
          </Dialog.Title>

          <form
            onSubmit={handleSubmit(handleFormSubmit)}
            className="mt-4 space-y-4"
          >
            {/* Title */}
            <div>
              <label
                htmlFor="task-form-title"
                className="block text-sm font-medium text-neutral-700 dark:text-neutral-300"
              >
                {t('task.titleLabel')}
              </label>
              <input
                {...register('title')}
                id="task-form-title"
                type="text"
                className={clsx(
                  'mt-1 w-full rounded-lg border px-3 py-2',
                  'bg-white text-neutral-900 dark:bg-neutral-800 dark:text-white',
                  errors.title
                    ? 'border-red-500'
                    : 'border-neutral-300 dark:border-neutral-700',
                  'focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500'
                )}
                placeholder={t('task.titlePlaceholder')}
                autoFocus
              />
              {errors.title && (
                <p className="mt-1 text-xs text-red-500">{t('task.titleRequired')}</p>
              )}
            </div>

            {/* Description */}
            <div>
              <label
                htmlFor="task-form-description"
                className="block text-sm font-medium text-neutral-700 dark:text-neutral-300"
              >
                {t('task.description')}
              </label>
              <textarea
                {...register('description')}
                id="task-form-description"
                rows={3}
                className={clsx(
                  'mt-1 w-full rounded-lg border px-3 py-2',
                  'bg-white text-neutral-900 dark:bg-neutral-800 dark:text-white',
                  'border-neutral-300 dark:border-neutral-700',
                  'focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500'
                )}
                placeholder={t('task.descriptionPlaceholder')}
              />
            </div>

            {/* Task Type & Priority */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label
                  htmlFor="task-form-type"
                  className="block text-sm font-medium text-neutral-700 dark:text-neutral-300"
                >
                  {t('task.typeLabel')}
                </label>
                <select
                  {...register('taskType')}
                  id="task-form-type"
                  className={clsx(
                    'mt-1 w-full rounded-lg border px-3 py-2',
                    'bg-white text-neutral-900 dark:bg-neutral-800 dark:text-white',
                    'border-neutral-300 dark:border-neutral-700',
                    'focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500'
                  )}
                >
                  {taskTypeOptions.map((type) => (
                    <option key={type.value} value={type.value}>
                      {t(`task.taskType.${type.value}`)}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label
                  id="task-form-priority-label"
                  className="block text-sm font-medium text-neutral-700 dark:text-neutral-300"
                >
                  {t('task.priority')}
                </label>
                <div
                  role="group"
                  aria-labelledby="task-form-priority-label"
                  className="mt-1 flex flex-wrap gap-1"
                >
                  {priorityOptions.map((option) => (
                    <button
                      key={option.value}
                      type="button"
                      onClick={() =>
                        setValue('priority', option.value as TaskFormData['priority'])
                      }
                      aria-pressed={priority === option.value}
                      className={clsx(
                        'flex-1 whitespace-nowrap rounded-lg px-2 py-2 text-xs font-medium',
                        'transition-all',
                        priority === option.value
                          ? `${option.color} text-white`
                          : 'bg-neutral-100 text-neutral-600 hover:bg-neutral-200 dark:bg-neutral-800 dark:text-neutral-400'
                      )}
                    >
                      {t(`task.priorities.${option.value}`)}
                    </button>
                  ))}
                </div>
              </div>
            </div>

            {/* Due Date */}
            <div>
              <label
                htmlFor="task-form-due-date"
                className="block text-sm font-medium text-neutral-700 dark:text-neutral-300"
              >
                <Calendar className="mr-1 inline h-4 w-4" />
                {t('task.dueDate')}
              </label>
              <input
                {...register('dueDate')}
                id="task-form-due-date"
                type="date"
                className={clsx(
                  'mt-1 w-full rounded-lg border px-3 py-2',
                  'bg-white text-neutral-900 dark:bg-neutral-800 dark:text-white',
                  'border-neutral-300 dark:border-neutral-700',
                  'focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500'
                )}
              />
            </div>

            {/* Assignee */}
            <div>
              <label
                id="task-form-assignee-label"
                className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-1"
              >
                <User className="mr-1 inline h-4 w-4" />
                {t('task.assignee')}
              </label>
              <AssigneePicker
                organizationId={currentOrgId ?? ''}
                projectMembers={projectMembers}
                currentAssignee={selectedAssignee}
                onSelect={handleAssigneeChange}
                disabled={isSubmitting}
              />
            </div>

            {/* Time Estimate */}
            <div>
              <label
                htmlFor="task-form-estimated-minutes"
                className="block text-sm font-medium text-neutral-700 dark:text-neutral-300"
              >
                {t('task.estimatedMinutes')}
              </label>
              <input
                {...register('estimatedMinutes', { valueAsNumber: true })}
                id="task-form-estimated-minutes"
                type="number"
                min="0"
                step="15"
                className={clsx(
                  'mt-1 w-full rounded-lg border px-3 py-2',
                  'bg-white text-neutral-900 dark:bg-neutral-800 dark:text-white',
                  'border-neutral-300 dark:border-neutral-700',
                  'focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500'
                )}
                placeholder={t('task.estimatedMinutesPlaceholder')}
              />
            </div>

            {/* Labels */}
            <div>
              <label
                id="task-form-labels-label"
                className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-1"
              >
                <Tag className="mr-1 inline h-4 w-4" />
                {t('task.labels')}
              </label>
              <div
                role="group"
                aria-labelledby="task-form-labels-label"
                className="flex flex-wrap items-center gap-2"
              >
                {selectedLabels.map((label) => (
                  <Badge
                    key={label.id}
                    color={label.color}
                    size="md"
                    onRemove={() => handleRemoveLabel(label.id)}
                  >
                    {label.name}
                  </Badge>
                ))}
                <LabelPicker
                  selectedLabels={selectedLabels}
                  availableLabels={availableLabels}
                  onAddLabel={handleAddLabel}
                  onRemoveLabel={handleRemoveLabel}
                  onCreateLabel={handleCreateLabel}
                  compact={selectedLabels.length > 0}
                />
              </div>
            </div>

            {/* Actions */}
            <div className="flex justify-end gap-3 pt-4">
              <button
                type="button"
                onClick={handleClose}
                className={clsx(
                  'rounded-lg px-4 py-2 text-sm font-medium',
                  'text-neutral-700 hover:bg-neutral-100',
                  'dark:text-neutral-300 dark:hover:bg-neutral-800'
                )}
              >
                {t('common.cancel')}
              </button>
              <button
                type="submit"
                disabled={isSubmitting}
                className={clsx(
                  'rounded-lg px-4 py-2 text-sm font-medium',
                  'bg-primary-600 text-white hover:bg-primary-700',
                  'disabled:cursor-not-allowed disabled:opacity-50'
                )}
              >
                {isSubmitting
                  ? t('common.saving')
                  : isEdit
                    ? t('common.saveChanges')
                    : t('task.create')}
              </button>
            </div>
          </form>

          <Dialog.Close asChild>
            <button
              className={clsx(
                'absolute right-4 top-4 rounded p-1',
                'text-neutral-400 hover:bg-neutral-100 hover:text-neutral-500',
                'dark:hover:bg-neutral-800'
              )}
              aria-label={t('common.close')}
            >
              <X className="h-4 w-4" />
            </button>
          </Dialog.Close>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}
